/**
 * "My Order" — the panel down the right of the design.
 *
 * Every call in here returns the **whole panel**, and the component simply
 * replaces its cached copy. That is not laziness: the totals depend on every
 * line, and a stepper that updated one row and then fetched the summary would
 * show a merchant a quantity and a total that disagreed for as long as the
 * second request took.
 *
 * "Estimated Total" keeps the word estimated for a reason the interface must not
 * lose. Scheduled lines have no price yet — their group buy has not closed — so
 * the figure is an upper bound for them. It will never be an underestimate: a
 * pool that reaches no bulk rung falls back to exactly this price.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Truck, Users } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import { Button, Card, EmptyState, Pill, SkeletonRows } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";
import {
  marketplaceRepository,
  type Basket,
  type BasketLine,
  type FulfilmentMethod,
} from "@/repositories/marketplaceRepository";

import { RetailerMark } from "./MarketplaceShowcase";

export function MarketplaceBasket() {
  const client = useQueryClient();
  const [method, setMethod] = useState<FulfilmentMethod>("COLLECTION");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [error, setError] = useState("");

  const basket = useQuery({
    queryKey: ["marketplace", "basket"],
    queryFn: () => marketplaceRepository.basket(),
  });

  const replace = (next: Basket) =>
    client.setQueryData(["marketplace", "basket"], next);

  const checkout = useMutation({
    mutationFn: () =>
      marketplaceRepository.checkout({
        fulfilment_method: method,
        delivery_address: method === "DELIVERY" ? address : undefined,
        delivery_postal_code: method === "DELIVERY" ? postalCode : undefined,
      }),
    onSuccess: () => {
      setError("");
      void basket.refetch();
      void client.invalidateQueries({ queryKey: ["marketplace", "orders"] });
      void client.invalidateQueries({
        queryKey: ["marketplace", "my-group-buys"],
      });
    },
    onError: (failure) =>
      setError(
        failure instanceof ApiError ? failure.detail : "Something went wrong.",
      ),
  });

  if (basket.isLoading || !basket.data) {
    return (
      <Card>
        <SkeletonRows rows={4} />
      </Card>
    );
  }

  const { lines, summary } = basket.data;

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink-900">My order</h2>
        <Pill tone={summary.item_count > 0 ? "success" : "neutral"}>
          {summary.item_count}
        </Pill>
      </div>

      {lines.length === 0 ? (
        <EmptyState
          title="Nothing yet"
          description="Add products and they appear here."
        />
      ) : (
        <>
          <ul className="divide-y divide-line">
            {lines.map((line) => (
              <BasketRow key={line.id} line={line} onChanged={replace} />
            ))}
          </ul>

          <dl className="space-y-1.5 border-t border-line pt-3 text-sm">
            <Row label="Subtotal" value={summary.subtotal} />
            {Number(summary.estimated_saving) > 0 && (
              <Row
                label="Estimated savings"
                value={`-${summary.estimated_saving}`}
                money
              />
            )}
            <Row
              label="Estimated total"
              value={summary.estimated_total}
              strong
            />
          </dl>

          {summary.scheduled_line_count > 0 && (
            <p className="rounded-control bg-info-50 p-2 text-xs text-info-500">
              {summary.scheduled_line_count} item(s) are scheduled to buy with
              other shops. Their final price is set when the group buy closes,
              and will never be more than shown here.
            </p>
          )}

          <div className="grid grid-cols-2 gap-2">
            <MethodButton
              active={method === "COLLECTION"}
              onClick={() => setMethod("COLLECTION")}
              icon={<Users className="size-4" />}
            >
              Collection
            </MethodButton>
            <MethodButton
              active={method === "DELIVERY"}
              onClick={() => setMethod("DELIVERY")}
              icon={<Truck className="size-4" />}
            >
              Delivery
            </MethodButton>
          </div>

          {method === "DELIVERY" && (
            <div className="space-y-2">
              <input
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                placeholder="Delivery address"
                aria-label="Delivery address"
                className="h-touch w-full rounded-control border border-line px-3 text-sm"
              />
              <input
                value={postalCode}
                onChange={(event) => setPostalCode(event.target.value)}
                placeholder="Postal code"
                aria-label="Postal code"
                inputMode="numeric"
                className="h-touch w-full rounded-control border border-line px-3 text-sm"
              />
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-danger-700">
              {error}
            </p>
          )}

          <Button
            variant="money"
            fullWidth
            loading={checkout.isPending}
            onClick={() => checkout.mutate()}
          >
            Place order
          </Button>

          {summary.wholesaler_count > 1 && (
            <p className="text-center text-xs text-ink-500">
              This will become {summary.wholesaler_count} orders, one per
              supplier.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

function BasketRow({
  line,
  onChanged,
}: {
  line: BasketLine;
  onChanged: (basket: Basket) => void;
}) {
  const change = useMutation({
    mutationFn: (quantity: number) =>
      marketplaceRepository.setLineQuantity(line.id, quantity),
    onSuccess: onChanged,
  });

  const remove = useMutation({
    mutationFn: () => marketplaceRepository.removeLine(line.id),
    onSuccess: onChanged,
  });

  return (
    <li className="flex items-start gap-2 py-2">
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-xs font-medium text-ink-900">
          {line.listing.name}
        </p>
        <p className="text-xs text-ink-500">
          <RetailerMark supplier={line.listing.wholesaler} />
        </p>
        {line.scheduled_for && (
          <p className="mt-0.5 text-xs text-info-500">
            Group buy for {formatDate(line.scheduled_for)}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1">
        <span className="text-xs font-medium tabular-nums text-ink-900">
          {formatMoney(line.indicative_total)}
        </span>
        <select
          value={line.quantity}
          onChange={(event) => change.mutate(Number(event.target.value))}
          aria-label={`Quantity of ${line.listing.name}`}
          className="h-8 rounded-control border border-line px-1 text-xs"
        >
          {Array.from({ length: 20 }, (_, index) => index + 1).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => remove.mutate()}
          aria-label={`Remove ${line.listing.name}`}
          className="p-1 text-ink-400 hover:text-danger-700"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </li>
  );
}

function MethodButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex h-touch items-center justify-center gap-2 rounded-control border text-sm font-medium",
        active
          ? "border-brand-500 bg-brand-50 text-brand-600"
          : "border-line bg-surface text-ink-600",
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function Row({
  label,
  value,
  strong,
  money,
}: {
  label: string;
  value: string;
  strong?: boolean;
  money?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className={strong ? "font-medium text-ink-900" : "text-ink-500"}>
        {label}
      </dt>
      <dd
        className={cn(
          "tabular-nums",
          strong ? "font-medium text-ink-900" : "text-ink-600",
          money && "text-money-700",
        )}
      >
        {formatMoney(value)}
      </dd>
    </div>
  );
}
